import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Box, CircularProgress, Divider, Button, TextField, DialogActions, DialogContentText, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import NotesIcon from '@mui/icons-material/Notes';
import { getContact, updateContact, deleteContact } from '../api/contacts';
import { getPurchasesByContact, exportPurchases } from '../api/purchases';
import { ContactInterface, PurchaseInterface } from '../types';
import AddPurchaseModal from './AddPurchaseModal';
import PurchasesTable from './PurchasesTable';
import SendPaymentLinkModal from './SendPaymentLinkModal';
import TimelineNotesModal from './TimelineNotesModal';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import WhatsAppModal from './WhatsAppModal';

interface ContactDetailsModalProps {
  open: boolean;
  contactId: string | null;
  onClose: () => void;
  onContactChanged?: () => void;
}

function ContactDetailsModal({ open, contactId, onClose, onContactChanged }: ContactDetailsModalProps): React.JSX.Element {
  const [contact, setContact] = useState<ContactInterface | null>(null);
  const [purchases, setPurchases] = useState<PurchaseInterface[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [purchasesLoading, setPurchasesLoading] = useState<boolean>(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState<Partial<ContactInterface>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addPurchaseOpen, setAddPurchaseOpen] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [paymentLinkModalOpen, setPaymentLinkModalOpen] = useState(false);
  const [timelineNotesModalOpen, setTimelineNotesModalOpen] = useState(false);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [selectedContactForWhatsApp, setSelectedContactForWhatsApp] = useState<{ name: string, phone: string } | null>(null);

  const [localPurchasesSearchInput, setLocalPurchasesSearchInput] = useState('');
  const [purchasesSearchQuery, setPurchasesSearchQuery] = useState('');
  const [purchasesSortBy, setPurchasesSortBy] = useState('created_at');
  const [purchasesSortOrder, setPurchasesSortOrder] = useState<'asc' | 'desc'>('desc');

  const fetchContact = useCallback(async (showLoader = true): Promise<void> => {
    if (!contactId) return;
    if (showLoader) setLoading(true);
    try {
      const contactData = (await getContact(contactId));
      setContact(contactData);
      setEditData(contactData);
    } catch (error) {
      console.error('Error fetching contact:', error);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [contactId]);

  const fetchPurchases = useCallback(async (): Promise<void> => {
    if (!contactId) return;
    setPurchasesLoading(true);
    try {
      // In a real paginated endpoint, we'd use page/pageSize, but the backend accepts sort/search now
      let queryParams = `contact_id=${contactId}`;
      if (purchasesSearchQuery) {
        queryParams += `&method=search&variant_name_raw=${encodeURIComponent(purchasesSearchQuery)}`;
      }
      if (purchasesSortBy) {
        queryParams += `&sort_by=${purchasesSortBy}&sort_order=${purchasesSortOrder}`;
      }
      const response = await getPurchasesByContact(queryParams);
      setPurchases(response.data || []);
    } catch (error) {
      console.error('Error fetching purchases:', error);
    } finally {
      setPurchasesLoading(false);
    }
  }, [contactId, purchasesSearchQuery, purchasesSortBy, purchasesSortOrder]);


  useEffect(() => {
    if (open && contactId) {
      fetchContact();
      fetchPurchases();
      setEditMode(false);
    }
  }, [open, contactId, fetchContact, fetchPurchases]);

  const handleClose = (): void => {
    setContact(null);
    setPurchases([]);
    setEditMode(false);
    onClose();
  };

  const handleEdit = () => setEditMode(true);
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditData(contact!);
  };

  const handleChange = (field: keyof ContactInterface) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    if (!contact) return;
    setSaving(true);
    try {
      await updateContact(contact.id, editData);
      setEditMode(false);
      fetchContact();
      if (onContactChanged) onContactChanged();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!contact) return;
    setDeleting(true);
    try {
      await deleteContact(contact.id);
      setDeleteDialogOpen(false);
      handleClose();
      if (onContactChanged) onContactChanged();
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const handleOpenAddPurchase = () => setAddPurchaseOpen(true);
  const handleCloseAddPurchase = () => setAddPurchaseOpen(false);
  const handlePurchaseCreated = () => { fetchPurchases(); handleCloseAddPurchase(); };

  const handlePurchasesSortChange = (column: string) => {
    if (purchasesSortBy === column) {
      setPurchasesSortOrder(purchasesSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setPurchasesSortBy(column);
      setPurchasesSortOrder('asc');
    }
  };

  const handlePurchasesSearchClick = () => {
    setPurchasesSearchQuery(localPurchasesSearchInput);
  };

  const handlePurchasesKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handlePurchasesSearchClick();
    }
  };

  const handlePurchasesResetSearch = () => {
    setLocalPurchasesSearchInput('');
    setPurchasesSearchQuery('');
  };

  const handleExportPurchases = async () => {
    if (!contact) return;
    let params = `contact_id=${contact.id}`;
    if (purchasesSearchQuery) {
      params += `&method=search&variant_name_raw=${encodeURIComponent(purchasesSearchQuery)}`;
    }
    if (purchasesSortBy) {
      params += `&sort_by=${purchasesSortBy}&sort_order=${purchasesSortOrder}`;
    }
    await exportPurchases(params);
  };



  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          פרטי תלמיד
          <Box sx={{ position: 'absolute', right: 8, top: 8, display: 'flex', gap: 1 }}>
            {!editMode && <IconButton onClick={handleEdit}><EditIcon /></IconButton>}
            {!editMode && <IconButton color="error" onClick={() => setDeleteDialogOpen(true)}><DeleteIcon /></IconButton>}
            <IconButton aria-label="close" onClick={handleClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : contact ? (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      שם השחיין
                    </Typography>
                    {editMode ? (
                      <TextField value={editData.name || ''} onChange={handleChange('name')} size="small" fullWidth />
                    ) : (
                      <Typography variant="body1">{contact.name || 'N/A'}</Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      גיל
                    </Typography>
                    {editMode ? (
                      <TextField value={editData.age ?? ''} onChange={handleChange('age')} size="small" fullWidth type="number" />
                    ) : (
                      <Typography variant="body1">{contact.age ?? 'N/A'}</Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      שם ההורה
                    </Typography>
                    {editMode ? (
                      <TextField value={editData.parent_name || ''} onChange={handleChange('parent_name')} size="small" fullWidth />
                    ) : (
                      <Typography variant="body1">{contact.parent_name || 'N/A'}</Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      טלפון הורה
                    </Typography>
                    {editMode ? (
                      <TextField value={editData.phone || ''} onChange={handleChange('phone')} size="small" fullWidth />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">{contact.phone || 'N/A'}</Typography>
                        {contact.phone && (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedContactForWhatsApp({ name: contact.name, phone: contact.phone });
                              setWhatsAppModalOpen(true);
                            }}
                          >
                            <WhatsAppIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      אימייל הורה
                    </Typography>
                    {editMode ? (
                      <TextField value={editData.email || ''} onChange={handleChange('email')} size="small" fullWidth />
                    ) : (
                      <Typography variant="body1">{contact.email || 'N/A'}</Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              {!editMode && (
                <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
                  <Button variant="contained" color="secondary" onClick={() => setPaymentLinkModalOpen(true)}>
                    שליחת קישור לתשלום
                  </Button>
                  <Button variant="contained" color="info" onClick={() => setTimelineNotesModalOpen(true)} startIcon={<NotesIcon />}>
                    הערות
                  </Button>
                </Box>
              )}

              {editMode && (
                <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                    {saving ? <CircularProgress size={20} /> : 'שמור'}
                  </Button>
                  <Button variant="outlined" color="secondary" startIcon={<CancelIcon />} onClick={handleCancelEdit} disabled={saving}>
                    ביטול
                  </Button>
                </Box>
              )}

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h6" gutterBottom>
                    הזמנות
                  </Typography>
                  <IconButton color="primary" onClick={handleOpenAddPurchase} sx={{ backgroundColor: 'primary.main', color: 'white', '&:hover': { backgroundColor: 'primary.dark' } }}>
                    <AddIcon />
                  </IconButton>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    label="חיפוש לפי קורס"
                    variant="outlined"
                    size="small"
                    value={localPurchasesSearchInput}
                    onChange={(e) => setLocalPurchasesSearchInput(e.target.value)}
                    onKeyPress={handlePurchasesKeyPress}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minWidth: 250 }}
                  />
                  <Button variant="contained" onClick={handlePurchasesSearchClick}>חיפוש</Button>
                  {(localPurchasesSearchInput || purchasesSearchQuery) && (
                    <IconButton onClick={handlePurchasesResetSearch} title="נקה חיפוש">
                      <ClearIcon />
                    </IconButton>
                  )}
                </Box>
                <PurchasesTable
                  displayName={false}
                  purchases={purchases}
                  loading={purchasesLoading}
                  page={1}
                  pageSize={100}
                  total={purchases.length}
                  onPageChange={() => { }}
                  onRefresh={fetchPurchases}
                  sortBy={purchasesSortBy}
                  sortOrder={purchasesSortOrder}
                  onSortChange={handlePurchasesSortChange}
                  onExport={handleExportPurchases}
                  title="קורסים"
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  System Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Contact ID
                    </Typography>
                    <Typography variant="body1">{contact.id}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Created At
                    </Typography>
                    <Typography variant="body1">{formatDate(contact.created_at)}</Typography>
                  </Box>
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      הערות כלליות
                    </Typography>
                    {editMode ? (
                      <TextField
                        value={editData.notes || ''}
                        onChange={handleChange('notes')}
                        size="small"
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={8}
                      />
                    ) : contact.notes && contact.notes.trim() !== '' ? (
                      <Button
                        variant="outlined"
                        startIcon={<NotesIcon />}
                        onClick={() => setShowNotesModal(true)}
                        sx={{ mt: 1 }}
                      >
                        הערות כלליות
                      </Button>
                    ) : (
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>אין הערות כלליות</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            <Typography variant="body1" color="text.secondary">
              תלמיד לא נמצא
            </Typography>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>מחיקת תלמיד</DialogTitle>
        <DialogContent>
          <DialogContentText>האם למחוק את התלמיד הזה?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>ביטול</Button>
          <Button onClick={handleDelete} color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'מחק'}
          </Button>
        </DialogActions>
      </Dialog>
      <AddPurchaseModal
        open={addPurchaseOpen}
        onClose={handleCloseAddPurchase}
        contactId={contactId || ''}
        onPurchaseCreated={handlePurchaseCreated}
      />
      {/* Notes Modal */}
      <Dialog open={showNotesModal} onClose={() => setShowNotesModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          הערות כלליות
          <IconButton
            aria-label="close"
            onClick={() => setShowNotesModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {contact?.notes}
          </Typography>
        </DialogContent>
      </Dialog>

      <SendPaymentLinkModal
        open={paymentLinkModalOpen}
        onClose={() => setPaymentLinkModalOpen(false)}
        contactId={contactId}
      />

      <TimelineNotesModal
        open={timelineNotesModalOpen}
        onClose={() => setTimelineNotesModalOpen(false)}
        contact={contact}
        onNotesUpdated={() => fetchContact(false)}
      />

      {selectedContactForWhatsApp && (
        <WhatsAppModal
          open={whatsAppModalOpen}
          onClose={() => {
            setWhatsAppModalOpen(false);
            setTimeout(() => setSelectedContactForWhatsApp(null), 300);
          }}
          contactName={selectedContactForWhatsApp.name}
          contactNumber={selectedContactForWhatsApp.phone}
        />
      )}
    </>
  );
}

export default ContactDetailsModal; 